import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, FileText } from 'lucide-react';
import { DemandDialog } from './DemandDialog';
import { useAuth } from '../../context/AuthContext';
import { COUNTRIES, Field, inputCls, money, LegalFooter } from './shared';

export const ChildSupportForm = ({ country }) => {
  const { api } = useAuth();
  const [f, setF] = useState({ payer_income: '', other_income: '', children: 1, monthly_needs: '', minimum_wage: '' });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [demandOpen, setDemandOpen] = useState(false);
  const cur = COUNTRIES.find((c) => c.code === country)?.currency;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const num = (v) => (v === '' ? null : Number(v));
      const { data } = await api.post('/family/child-support', {
        country, payer_income: Number(f.payer_income), children: Number(f.children),
        other_income: Number(f.other_income || 0), monthly_needs: num(f.monthly_needs), minimum_wage: num(f.minimum_wage),
      });
      setRes({ ...data, payer_income: Number(f.payer_income), other_income: Number(f.other_income || 0), children: Number(f.children), monthly_needs: num(f.monthly_needs), minimum_wage: num(f.minimum_wage) });
    } catch (err) {
      setRes(null);
      toast.error(err.response?.data?.detail || 'No se pudo calcular');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <form onSubmit={submit} className="space-y-4" data-testid="child-support-form">
        <Field label="Ingreso mensual del alimentante">
          <input data-testid="cs-payer-income" required type="number" min="1" value={f.payer_income} onChange={set('payer_income')} className={inputCls} />
        </Field>
        <Field label="Ingreso mensual del otro progenitor" hint="Se usa para repartir las necesidades en proporción">
          <input data-testid="cs-other-income" type="number" min="0" value={f.other_income} onChange={set('other_income')} className={inputCls} />
        </Field>
        <Field label="Número de hijos">
          <input data-testid="cs-children" type="number" min="1" max="10" value={f.children} onChange={set('children')} className={inputCls} />
        </Field>
        <Field label="Necesidades mensuales de los hijos (opcional)" hint="Colegio, salud, alimentación, vivienda. Si lo dejas vacío se usa un porcentaje orientativo">
          <input data-testid="cs-needs" type="number" min="0" value={f.monthly_needs} onChange={set('monthly_needs')} className={inputCls} />
        </Field>
        {country !== 'chile' && (
          <Field label="Salario mínimo vigente (opcional)">
            <input data-testid="cs-min-wage" type="number" min="0" value={f.minimum_wage} onChange={set('minimum_wage')} className={inputCls} />
          </Field>
        )}
        <button data-testid="cs-submit" disabled={loading} className="inline-flex items-center gap-2 rounded-full bg-rose-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-rose-400 transition-colors disabled:opacity-60">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Calcular pensión
        </button>
      </form>
      {res && (
        <div data-testid="cs-result" className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-5">
          <p className="text-xs uppercase tracking-wider text-rose-300">Pensión mensual estimada</p>
          <p data-testid="cs-amount" className="mt-1 text-4xl font-bold text-white font-['Outfit']">{money(res.monthly_amount, cur)}</p>
          <p className="text-sm text-slate-400">Rango: {money(res.range_low, cur)} – {money(res.range_high, cur)} · {money(res.per_child, cur)} por hijo</p>
          <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div><dt className="text-slate-500">Mínimo legal</dt><dd className="text-white">{money(res.legal_minimum, cur)}</dd></div>
            <div><dt className="text-slate-500">Tope legal</dt><dd className="text-white">{money(res.legal_cap, cur)}</dd></div>
            <div className="col-span-2"><dt className="text-slate-500">Método</dt><dd className="text-slate-200">{res.method}</dd></div>
          </dl>
          {res.notes.map((n) => <p key={n} className="mt-2 text-sm text-amber-300">{n}</p>)}
          <LegalFooter basis={res.legal_basis} disclaimer={res.disclaimer} />
          <button data-testid="cs-demand-btn" onClick={() => setDemandOpen(true)} className="mt-4 inline-flex items-center gap-2 rounded-full border border-rose-400/60 px-5 py-2 text-sm font-semibold text-rose-100 hover:bg-rose-500/10 transition-colors">
            <FileText className="h-4 w-4" /> Generar demanda en Word
          </button>
        </div>
      )}
      {res && demandOpen && <DemandDialog calc={res} open={demandOpen} onClose={() => setDemandOpen(false)} />}
    </div>
  );
};
